<template>
  <div class="w-full lg:pr-3 bar-main-container">
    <div class="hidden bar-container lg:flex lg:flex-row items-center font-rubik">
      <div class="selected-filters-container">
        <button v-for="filter in selectedFilters" :key="filter.text"
          @click="removeFilter(filter)"
          class="filter-button"
        >
          {{ filter.text }}
          <img class="inline pl-2" src="@/images/partners-page/clear.svg" :alt="`Clear ${filter.text} filter`">
        </button>
      </div>
      <div class="change-location">
        <div class="relative hidden lg:inline">
          <div class="header-input-container flex flex-row justify-around items-center">
            <input
              @keyup.enter="search()"
              v-model="query"
              class="bg-transparent w-full focus:ring-0 focus:outline-none"
              type="search"
              placeholder="Change location"
            />
            <img @click="search()" src="@/images/header/search.svg" alt="search" />
          </div>
          <!-- Dropdown container for results -->
          <div
            v-if="cities.length > 0 && query.length >= 3"
            class="absolute left-0 right-0 mt-1 border rounded-md bg-white z-10"
          >
            <div v-if="cities.length" role="menu" class="border rounded-md">
              <div class="dropdown-content modal-card-body">
                <p class="dropdown-item"><b>We found mulitple results...</b></p>
                <a v-for="city in cities" :key="city.id" :href="cityPath(city.node)" class="dropdown-item">
                  {{ city.node.name }}, {{ city.node.stateId | upcase }}
                </a>
              </div>
            </div>
          </div>
          <div v-if="error" class="help py-1 text-center bg-white text-red-500 border rounded-md">
            {{ error }}
            <b><a @click.prevent="resetSearch()">Try Again</a></b>
          </div>
        </div>
      </div>
    </div>
    <div class="lg:hidden bar-container font-rubik flex justify-between px-4">
      <div class="header-input-container flex flex-row justify-around items-center">
        <img src="@/images/header/search.svg" alt="search" />
        <input
          v-model="productName"
          class="bg-transparent w-full focus:ring-0 focus:outline-none"
          type="search"
          placeholder="Search products"
        />
      </div>
      <img @click="toggleMobileFilters" class="" src="@/images/header/filters.svg" alt="filters" />
    </div>
  </div>
</template>

<script>

import ahoy from 'shared/ahoy'
import { zipQuery, citySearchQuery, geoSearchQuery, solutionByCity } from 'shared/graphql/queries'
import { graphqlQuery } from 'shared/graphql'
import { cityPath } from '../../../shared/city-helper'
import { mapState } from 'vuex'
const ZIP_REGEX = /^\d{5}$/
const redirectToCity = (city) => window.location.href = cityPath(city)

export default {
  props: {
    products: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      selectedFilters: [],

      loading: 0,
      solutionSlug: '',
      error: null,
      query: '',
      coordinates: null,
      cities: [],
      productName: ''
    }
  },
  filters: {
    upcase(string) {
      return string.toUpperCase()
    }
  },
  created () {
    this.$bus.$on('selectedFilters', (val) => {
      this.selectedFilters = val
    })
  },
  beforeDestroy() {
    this.$bus.$off('selectedFilters')
  },
  computed: {
    ...mapState('shared', ['zipcode']),
    ...mapState('quote', ['lead'])
  },
  watch: {
    query() {
      if (this.query === '') {
        this.error = null
        this.cities = []
      } else if (this.error && this.query !== this.lastQuery) {
        this.error = null
      }
    },
    productName() {
      if (this.productName.length > 0) {
        this.searchProductByName(this.productName) 
      }
      else {
        this.$bus.$emit('filteredProducts', this.products)
      }
    }
  },
  methods: {
    cityPath,
    removeFilter(filter) {
      this.$bus.$emit('removeFilter', filter)
    },
    resetSearch() {
      this.query = ''
      this.error = null
    },
    getRequestBody() {
      if (ZIP_REGEX.test(this.query)) {
        return {
          query: zipQuery,
          variables: {
            id: this.query
          }
        }
      } else if (this.coordinates) {
        return {
          query: geoSearchQuery,
          variables: {
            coordinates: this.coordinates
          }
        }
      } else {
        return {
          query: citySearchQuery,
          variables: {
            query: this.query
          }
        }
      }
    },
    async trackSearch() {
      ahoy.track('location.search', {
        coordinates: this.coordinates,
        query: this.query
      })
    },
    async search() {      
      this.cities = []
      this.loading++
      this.lastQuery = this.query = this.query.trim()
      try {
        this.trackSearch()
        const result = await graphqlQuery(this.getRequestBody())
        return this.handleResult(result.data || {})
      } catch (e) {
        // handle error
        console.warn(e)
        this.error = 'There was an error while searching.'
        this.loading = 0
      }
    },
    async handleResult(result) {
      let { zipcode, zipcodes, cities } = result
      if (!zipcode && zipcodes && zipcodes.edges.length === 1) {
        zipcode = zipcodes.edges[0].node
      }
      if (this.solutionSlug && zipcode) {
        const data = await this.getSolutionPage(zipcode)
        if (data && data.solutionByCity && data.solutionByCity.solution) {
          if (this.lead) {
            await this.$store.dispatch('quote/lead.zipcode.update', {lead: this.lead, zipcode: zipcode.id})
          } else {
            this.$store.commit('shared/zipcode.set', zipcode.id)
          }
          this.$emit('close')
          this.loading = 0
          window.location.href = `/partners/${data.solutionByCity.organization.id}/${data.solutionByCity.solution.slug}`
          return
        }
      }
      if (zipcode) {
        if (this.lead) {
          await this.$store.dispatch('quote/lead.zipcode.update', {lead: this.lead, zipcode: zipcode.id})
        } else {
          this.$store.commit('shared/zipcode.set', zipcode.id)
        }
        this.$emit('close')
        return redirectToCity(zipcode.city)
      } else if (cities && cities.edges.length) {
        if (cities.edges.length === 1) {
          this.$emit('close')
          return redirectToCity(cities.edges[0].node)
        } else {
          this.cities = cities.edges
        }
      } else {
        this.error = 'Sorry, we couldn\'t find that location.'
      }
      this.loading = 0
    },
    async getSolutionPage(zipcode) {
      const body = {
        query: solutionByCity,
        variables: {
          cityId: zipcode.city.id,
          solutionSlug: this.solutionSlug
        }
      }
      const result = await graphqlQuery(body)
      return result.data
    },
    searchProductByName(searchString) {
      const searchTerms = searchString.toLowerCase().split(' ')
      const filteredProducts = this.products.filter((product) => {
        return searchTerms.every(term => product.name.toLowerCase().includes(term))
      })

      this.$bus.$emit('filteredProducts', filteredProducts)
    },
    toggleMobileFilters() {
      this.$bus.$emit('toggleMobileFilters')
    }
  }
}
</script>

<style scoped>
.bar-container {
  width: 100%;
  min-height: 47px;
  background-color: white;
  border: 1px solid #E2E1DE;
  border-radius: 12px;
  margin-bottom: 10px;
}
.selected-filters-container {
  width: 100%;
}
.change-location {
  width: 200px;
}
.filter-button{
  background: #EEECE9;
  color: black;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.0125em;
  height: 40px;
  margin-left: 1px;
  border: 1px solid #EEECE9;
  border-radius: 12px;
  padding: 0px 15px;
}
.header-input-container {
  width: 190px;
  height: 47px;
  padding: 18px 10px 18px 10px;
  gap: 5px;
  color: black;
}
.modal-card-body {
  max-height: 300px !important; 
  width: 250px; 
  margin-top: -4px;
  padding: 10px;
}

/* mobile */
@media(max-width: 1024px) {
  .bar-main-container {
    padding-left: 20px;
    padding-right: 20px;
    margin-top: 25px;
  }
  .header-input-container {
    width: 250px;
    height: 47px;
    padding: 0px;
    gap: 5px;
    color: black;
  }
}
</style>